/**
 * What the machine was SET to make, during each finished drum.
 *
 * The PLC records metres and drum changes; it does not record products. Which
 * product a drum holds is whatever the queue said at the time — so when a drum
 * change goes undetected, the record is wrong and nothing in the production
 * data can show it.
 *
 * `DiameterSetValue` can. It is the insulated-conductor diameter dialled in on
 * the panel, and it changes when the operator changes product. Crossed with
 * each execution's own window it answers the one question the records cannot:
 * was this drum made at the diameter its product needs?
 *
 * On 2026-09-15, L75:
 *   · a drum recorded as 23 660 m of N2XH 3x2.5 ran 191 minutes at Ø3.15 and
 *     then 34 minutes at Ø5.30 — the last stretch is a different product, and
 *     that is where the missed take-up change is
 *   · a drum recorded as N2XH 5x10 ran 83 minutes entirely at Ø4.25 while every
 *     other drum of that product ran at Ø5.30 — it is not that product at all
 *
 * Both are found here, both are reported as SUGGESTIONS. Nothing acts on them:
 * only the operator knows what was physically on the machine, and a system that
 * guessed would turn one wrong drum into ten.
 */

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

// Below this the line is not producing, so whatever the setpoint reads says
// nothing about what came off the drum. 5 m/min is under every threading crawl
// seen on these machines.
const PRODUCING_SPEED = 5;

// Two setpoints within this are the same setting.
//
// Deliberately tight. The PLC reports floats (3.1500000953674316 for 3.15) but
// every value is rounded to the hundredth before comparison, so all this has to
// absorb is the rounding itself. Anything wider is dangerous: on 16 September
// the L75 ran at Ø3.08 and then Ø3.10, two hundredths apart and genuinely
// different products — a tolerance of 0.02 would have called them the same and
// missed exactly the kind of switch this exists to catch.
const SAME_DIAMETER = 0.005;

// A stretch shorter than this is the operator dialling the next product in
// while the current drum finishes, not a product change mid-drum.
const MIN_STRETCH_MINUTES = 5;

const round2 = (v) => Math.round(num(v) * 100) / 100;

/** The diameter samples inside a window, in order, while the line was running. */
function producingSamples(samples, from, to) {
  return (Array.isArray(samples) ? samples : [])
    .filter(
      (s) =>
        s &&
        Number.isFinite(Number(s.ts)) &&
        s.ts >= from &&
        s.ts <= to &&
        num(s.speed) >= PRODUCING_SPEED &&
        Number.isFinite(Number(s.diameter))
    )
    .sort((a, b) => a.ts - b.ts);
}

/**
 * The consecutive stretches of one setpoint inside a window.
 *
 * Consecutive, not grouped: an operator who goes 3.15 → 5.30 → 3.15 did two
 * different things, and a plain tally of minutes per value would hide the
 * order — which is the whole of the answer when the question is "from where
 * did this stop being the product it says it is".
 */
export function diameterStretches(samples, from, to) {
  const inWindow = producingSamples(samples, from, to);
  const out = [];

  for (const s of inWindow) {
    const d = round2(s.diameter);
    const last = out[out.length - 1];
    if (last && Math.abs(last.diameter - d) <= SAME_DIAMETER) {
      last.to = s.ts;
      last.minutes += 1;
      last.metres += num(s.speed); // one sample per minute → m/min is metres
      continue;
    }
    out.push({ diameter: d, from: s.ts, to: s.ts, minutes: 1, metres: num(s.speed) });
  }

  return out.map((x) => ({ ...x, metres: Math.round(x.metres) }));
}

/**
 * Read one execution against the machine's diameter profile.
 *
 * @returns {{ stretches, dominant, changedMidDrum, suggestion }}
 *   `suggestion` is null when nothing looks wrong. When the setpoint changed
 *   part-way it carries the moment and an ESTIMATE of the metres produced after
 *   it — estimated because it is integrated from the speed samples, which is
 *   close but is not the meter. The operator confirms the number.
 */
export function readExecution(execution, samples) {
  const from = num(execution?.startedAt) || num(execution?.completedAt);
  const to = num(execution?.completedAt);
  const stretches = diameterStretches(samples, from, to);

  if (!stretches.length) {
    return { stretches: [], dominant: null, changedMidDrum: false, suggestion: null };
  }

  // Ranked by metres, not minutes: a stretch at 150 m/min is more of the drum
  // than a longer one at 20.
  const byMetres = [...stretches].sort((a, b) => b.metres - a.metres);
  const dominant = byMetres[0].diameter;

  const real = stretches.filter((s) => s.minutes >= MIN_STRETCH_MINUTES);
  const distinct = [...new Set(real.map((s) => s.diameter))];
  const changedMidDrum = distinct.length > 1;

  let suggestion = null;
  if (changedMidDrum) {
    // The first stretch that is not the dominant setting, after the dominant
    // one has run: that transition is where the drum stopped being what it says.
    const idx = real.findIndex(
      (s, i) => i > 0 && Math.abs(s.diameter - real[i - 1].diameter) > SAME_DIAMETER
    );
    const cut = idx > 0 ? real[idx] : null;
    if (cut) {
      const after = real.slice(idx).reduce((sum, s) => sum + s.metres, 0);
      const produced = num(execution?.producedLength);
      suggestion = {
        kind: 'CHANGED_MID_DRUM',
        splitAt: cut.from,
        fromDiameter: real[idx - 1].diameter,
        toDiameter: cut.diameter,
        // Rounded to the metre; it is an integration of speed, not a reading.
        metresAfterEstimate: Math.round(after),
        keepLengthEstimate: Math.max(0, Math.round((produced - after) * 100) / 100)
      };
    }
  }

  return { stretches, dominant, changedMidDrum, suggestion };
}

/**
 * Read a whole day of executions together.
 *
 * The second signal only exists at this level: a drum whose setpoint disagrees
 * with the OTHER drums of the same product. No standard is consulted and no
 * expected value is configured anywhere — the product's own other drums say
 * what it is made at, which is both simpler and harder to get wrong.
 *
 * @param executions ExecutionLog rows (any order)
 * @param samples    [{ ts, diameter, speed }] covering them
 * @returns Map keyed by the execution's `_id`
 */
export function fingerprintExecutions(executions, samples) {
  const list = (Array.isArray(executions) ? executions : []).filter(Boolean);
  const read = new Map();

  for (const ex of list) {
    read.set(String(ex._id), { execution: ex, ...readExecution(ex, samples) });
  }

  // What each product was mostly made at, weighted by the metres of each drum.
  const byJob = new Map();
  for (const { execution, dominant } of read.values()) {
    if (dominant == null) continue;
    const jobId = String(execution.jobId || '');
    if (!byJob.has(jobId)) byJob.set(jobId, new Map());
    const tally = byJob.get(jobId);
    tally.set(dominant, (tally.get(dominant) || 0) + num(execution.producedLength));
  }

  const productDiameter = new Map();
  for (const [jobId, tally] of byJob) {
    const ranked = [...tally.entries()].sort((a, b) => b[1] - a[1]);
    // Only meaningful once the product has more than one setting to compare —
    // a product with a single drum cannot be the odd one out of itself.
    if (ranked.length) productDiameter.set(jobId, ranked[0][0]);
  }

  for (const entry of read.values()) {
    const { execution, dominant } = entry;
    if (dominant == null || entry.suggestion) continue;

    const expected = productDiameter.get(String(execution.jobId || ''));
    if (expected == null || Math.abs(expected - dominant) <= SAME_DIAMETER) continue;

    // Only a clear majority is evidence. A product whose drums split two and
    // two says nothing about which half is wrong, and picking the heavier side
    // would be a coin toss presented as a finding.
    const siblings = [...read.values()].filter(
      (e) => String(e.execution.jobId) === String(execution.jobId)
    );
    const agreeing = siblings.filter(
      (e) => e.dominant != null && Math.abs(e.dominant - expected) <= SAME_DIAMETER
    ).length;
    if (agreeing < 2 || agreeing <= siblings.length - agreeing) continue;

    entry.suggestion = {
      kind: 'ODD_ONE_OUT',
      dominant,
      expected,
      agreeing,
      total: siblings.length
    };
  }

  return read;
}
